import { useState, useEffect } from 'react';
import { supabase } from '../../../services/supabase';
import { useAuth } from '../../../contexts/AuthContext';

export function usePendingDeliveries() {
  const { user } = useAuth();
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPendingDeliveries = async () => {
      try {
        if (!user) return;
        
        if (isMounted) setLoading(true);
        
        const { data: salesData, error: salesError } = await supabase
          .from('sales')
          .select(`
            *,
            sale_items (
              *,
              lot:lots (
                name,
                unit_sign,
                display_id
              )
            )
          `)
          .eq('auth_user_id', user.id)
          .in('delivery_status', ['undelivered', 'delivered'])
          .eq('status', 'confirmed')
          .order('created_at', { ascending: false });

        if (salesError) throw salesError;

        if (!isMounted) return;

        // Transformar los datos para el formato que espera el componente
        const formattedDeliveries = salesData.map(sale => ({
          id: sale.id,
          display_id: sale.display_id,
          client: sale.client?.name || 'Sin cliente',
          items: sale.sale_items.reduce((sum, item) => sum + item.quantity, 0),
          total: sale.total_amount,
          status: sale.payment_status === 'paid' ? 'Listo para entregar' : 'Preparación',
          created_at: sale.created_at,
          sale_items: sale.sale_items,
          delivery_status: sale.delivery_status,
          delivered_by: sale.delivered_by,
          delivered_at: sale.delivered_at
        }));

        if (isMounted) {
          setPendingDeliveries(formattedDeliveries);
          setError(null);
        }
      } catch (error) {
        console.error('Error fetching pending deliveries:', error);
        if (isMounted) {
          setError(error.message);
          setPendingDeliveries([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // Suscribirse a cambios en la tabla de ventas
    const subscription = supabase
      .channel('sales_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'sales',
          filter: `auth_user_id=eq.${user?.id}`
        }, 
        () => {
          if (isMounted) fetchPendingDeliveries();
        }
      )
      .subscribe();

    fetchPendingDeliveries();

    // Cleanup function
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [user]);

  const pendingCount = pendingDeliveries.filter(d => d.delivery_status === 'undelivered').length;

  return {
    pendingDeliveries,
    loading,
    error,
    totalPending: pendingCount,
    setPendingDeliveries
  };
} 